<template>
  <div class="alert-box p-4" :class="containerClass">
    <div class="flex">
      <div class="flex-shrink-0">
        <div v-if="$slots.icon" :class="iconClass">
          <slot name="icon" />
        </div>
        <template v-else>
          <component :is="iconComponent" :class="iconClass" aria-hidden="true" />
        </template>
      </div>
      <div class="ml-3 w-full">
        <h1 v-if="$slots.title" class="m-0 mb-4 text-sm font-semibold" :class="textClass">
          <slot name="title"></slot>
        </h1>
        <div class="mb-0 text-sm" :class="textClass">
          <slot></slot>
        </div>
        <div v-if="$slots.actions" class="mb-0 mt-4 text-sm" :class="textClass">
          <slot name="actions"></slot>
        </div>
      </div>
      <div v-if="$props.closable" class="ml-auto pl-3">
        <div class="-mx-1.5 -my-1.5">
          <button
            type="button"
            class="inline-flex p-1.5 text-neutral-80 hover:text-neutral-70 focus:outline-none"
            @click="close"
          >
            <span class="sr-only">Dismiss</span>
            <XIcon class="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";

// Icons
import { InformationCircleIcon, ExclamationIcon, CheckCircleIcon, XCircleIcon, XIcon } from "@heroicons/vue/solid";

type Themes = "info" | "success" | "warning" | "danger";

const props = withDefaults(
  defineProps<{
    theme?: Themes;
    rounded?: boolean;
    closable?: boolean;
  }>(),
  {
    theme: "info",
    rounded: true,
    closable: false,
  },
);

const emit = defineEmits<{
  (e: "close"): void;
}>();

const close = () => {
  emit("close");
};

const iconComponent = computed(() => {
  if (props.theme === "warning") return ExclamationIcon;
  if (props.theme === "danger") return XCircleIcon;
  if (props.theme === "success") return CheckCircleIcon;
  return InformationCircleIcon;
});

const iconClass = computed(() => {
  const classesByTheme: Record<Themes, string> = {
    info: "h-5 w-5 text-primary-60",
    warning: "h-5 w-5 text-warning-60",
    danger: "h-5 w-5 text-danger-60",
    success: "h-5 w-5 text-success-60",
  };
  return classesByTheme[props.theme];
});

const containerClass = computed(() => {
  const classes: Array<string> = [];
  classes.push(`alert-box-${props.theme}`);

  if (props.rounded) {
    classes.push("rounded-md");
  } else {
    classes.push("border-l-4 border-warning-60");
  }

  if (props.theme === "info") classes.push("bg-primary-99");
  else if (props.theme === "warning") classes.push("bg-warning-99");
  else if (props.theme === "danger") classes.push("bg-danger-99");
  else if (props.theme === "success") classes.push("bg-success-99");

  return classes.join(" ");
});

const textClass = computed(() => {
  const classesByTheme: Record<Themes, string> = {
    info: "text-primary-40",
    warning: "text-warning-5",
    danger: "text-danger-5",
    success: "text-success-5",
  };

  return classesByTheme[props.theme];
});
</script>

<style>
.alert-box p a {
  @apply font-semibold underline;
}
.alert-box-info p a {
  @apply text-primary-60;
}
.alert-box-info p a:hover {
  @apply text-primary-40;
}

.alert-box-warning p a {
  @apply text-warning-60;
}
.alert-box-warning p a:hover {
  @apply text-warning-40;
}

.alert-box-success p a {
  @apply text-success-60;
}
.alert-box-success p a:hover {
  @apply text-success-40;
}
</style>
